import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Checkbox,
    FormControlLabel,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    CircularProgress,
    Stepper,
    Step,
    StepLabel,
    Chip
} from '@mui/material';
import {
    GroupWork as GroupWorkIcon,
    PlayArrow as PlayArrowIcon,
    Delete as DeleteIcon,
    Pause as PauseIcon,
    CheckCircle as CheckCircleIcon,
    Email as EmailIcon,
    Upload as UploadIcon,
    Download as DownloadIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import userListingService from '../../services/userListingService';

/**
 * Page des actions groupées User ↔ Listings
 */
const UserListingsBulkActionsPage = () => {
    // États principaux
    const [selectedAction, setSelectedAction] = useState('');
    const [targetSelection, setTargetSelection] = useState('manual');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedListings, setSelectedListings] = useState([]);
    const [actionParams, setActionParams] = useState({});
    const [loading, setLoading] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);

    // Étapes du processus
    const [activeStep, setActiveStep] = useState(0);
    const steps = ['Sélection action', 'Configuration', 'Confirmation', 'Exécution'];

    // Actions disponibles
    const availableActions = [
        {
            id: 'suspend',
            name: 'Suspendre les annonces',
            description: 'Suspendre temporairement les annonces sélectionnées',
            icon: <PauseIcon />,
            color: 'warning',
            requiresReason: true
        },
        {
            id: 'activate',
            name: 'Activer les annonces',
            description: 'Réactiver les annonces suspendues',
            icon: <PlayArrowIcon />,
            color: 'success',
            requiresReason: false
        },
        {
            id: 'delete',
            name: 'Supprimer les annonces',
            description: 'Supprimer définitivement les annonces sélectionnées',
            icon: <DeleteIcon />,
            color: 'error',
            requiresReason: true,
            requiresConfirmation: true
        },
        {
            id: 'update_category',
            name: 'Changer de catégorie',
            description: 'Modifier la catégorie des annonces sélectionnées',
            icon: <CheckCircleIcon />,
            color: 'primary',
            requiresCategory: true
        },
        {
            id: 'send_notification',
            name: 'Envoyer notification',
            description: 'Envoyer une notification aux utilisateurs sélectionnés',
            icon: <EmailIcon />,
            color: 'info',
            requiresMessage: true
        }
    ];

    /**
     * Passer à l'étape suivante
     */
    const handleNext = () => {
        if (activeStep === steps.length - 1) {
            handleExecuteAction();
        } else {
            setActiveStep(prev => prev + 1);
        }
    };

    /**
     * Revenir à l'étape précédente
     */
    const handleBack = () => {
        setActiveStep(prev => prev - 1);
    };

    /**
     * Réinitialiser le processus
     */
    const handleReset = () => {
        setActiveStep(0);
        setSelectedAction('');
        setTargetSelection('manual');
        setSelectedUsers([]);
        setSelectedListings([]);
        setActionParams({});
        setResults(null);
        setError(null);
    };

    /**
     * Exécuter l'action groupée
     */
    const handleExecuteAction = async () => {
        try {
            setLoading(true);
            setError(null);

            const action = availableActions.find(a => a.id === selectedAction);
            if (!action) {
                throw new Error('Action non trouvée');
            }

            const bulkActionData = {
                action: selectedAction,
                userIds: selectedUsers,
                listingIds: selectedListings,
                params: actionParams
            };

            const response = await userListingService.performBulkAction(bulkActionData);
            setResults(response);
            setActiveStep(steps.length - 1);
        } catch (err) {
            console.error('Erreur lors de l\'exécution de l\'action:', err);
            setError('Erreur lors de l\'exécution de l\'action groupée');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Valider si on peut passer à l'étape suivante
     */
    const canProceedToNext = () => {
        switch (activeStep) {
            case 0:
                return selectedAction !== '';
            case 1:
                const action = availableActions.find(a => a.id === selectedAction);
                if (!action) return false;
                if (action.requiresReason && !actionParams.reason) return false;
                if (action.requiresCategory && !actionParams.newCategory) return false;
                if (action.requiresMessage && !actionParams.message) return false;
                return true;
            case 2:
                return (selectedUsers.length > 0 || selectedListings.length > 0);
            default:
                return false;
        }
    };

    /**
     * Étape 1: Sélection de l'action
     */
    const renderActionSelection = () => (
        <Grid container spacing={3}>
            {availableActions.map((action) => (
                <Grid item xs={12} md={6} key={action.id}>
                    <Card 
                        sx={{ 
                            cursor: 'pointer',
                            border: selectedAction === action.id ? 2 : 1,
                            borderColor: selectedAction === action.id ? `${action.color}.main` : 'divider'
                        }}
                        onClick={() => setSelectedAction(action.id)}
                    >
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box sx={{ color: `${action.color}.main`, mr: 2 }}>
                                    {action.icon}
                                </Box>
                                <Typography variant="h6">
                                    {action.name}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {action.description}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );

    /**
     * Étape 2: Configuration de l'action
     */
    const renderActionConfiguration = () => {
        const action = availableActions.find(a => a.id === selectedAction);
        if (!action) return null;

        return (
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Configuration: {action.name}
                </Typography>
                
                <Grid container spacing={3}>
                    {action.requiresReason && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Raison de l'action"
                                multiline
                                rows={3}
                                value={actionParams.reason || ''}
                                onChange={(e) => setActionParams(prev => ({ ...prev, reason: e.target.value }))}
                                placeholder="Expliquez la raison de cette action..."
                                required
                            />
                        </Grid>
                    )}

                    {action.requiresCategory && (
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth required>
                                <InputLabel>Nouvelle catégorie</InputLabel>
                                <Select
                                    value={actionParams.newCategory || ''}
                                    label="Nouvelle catégorie"
                                    onChange={(e) => setActionParams(prev => ({ ...prev, newCategory: e.target.value }))}
                                >
                                    <MenuItem value="services">Services</MenuItem>
                                    <MenuItem value="products">Produits</MenuItem>
                                    <MenuItem value="immobilier">Immobilier</MenuItem>
                                    <MenuItem value="vehicules">Véhicules</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    )}

                    {action.requiresMessage && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Message à envoyer"
                                multiline
                                rows={4}
                                value={actionParams.message || ''}
                                onChange={(e) => setActionParams(prev => ({ ...prev, message: e.target.value }))}
                                placeholder="Rédigez votre message..."
                                required
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Méthode de sélection</InputLabel>
                            <Select
                                value={targetSelection}
                                label="Méthode de sélection"
                                onChange={(e) => setTargetSelection(e.target.value)}
                            >
                                <MenuItem value="manual">Sélection manuelle</MenuItem>
                                <MenuItem value="filter">Par filtres</MenuItem>
                                <MenuItem value="file">Import fichier</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {action.requiresConfirmation && (
                        <Grid item xs={12}>
                            <Alert severity="warning">
                                <Typography variant="body2">
                                    ⚠️ Cette action est irréversible. Assurez-vous de votre choix avant de continuer.
                                </Typography>
                            </Alert>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        );
    };

    /**
     * Étape 3: Sélection des cibles
     */
    const renderTargetSelection = () => (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                Sélection des cibles
            </Typography>

            {targetSelection === 'manual' && (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="IDs des utilisateurs (séparés par des virgules)"
                            value={selectedUsers.join(', ')}
                            onChange={(e) => setSelectedUsers(e.target.value.split(',').map(id => id.trim()).filter(Boolean))}
                            placeholder="uuid1, uuid2, uuid3..."
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="IDs des annonces (séparés par des virgules)"
                            value={selectedListings.join(', ')}
                            onChange={(e) => setSelectedListings(e.target.value.split(',').map(id => id.trim()).filter(Boolean))}
                            placeholder="uuid1, uuid2, uuid3..."
                        />
                    </Grid>
                </Grid>
            )}

            {targetSelection === 'filter' && (
                <Alert severity="info">
                    La sélection par filtres sera disponible dans une prochaine version.
                </Alert>
            )}

            {targetSelection === 'file' && (
                <Box>
                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadIcon />}
                        sx={{ mb: 2 }}
                    >
                        Importer un fichier CSV
                        <input type="file" hidden accept=".csv" />
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                        Le fichier doit contenir les colonnes 'user_id' et/ou 'listing_id'
                    </Typography>
                </Box>
            )}

            <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Résumé de la sélection:
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Chip 
                        label={`${selectedUsers.length} utilisateur(s)`} 
                        color={selectedUsers.length > 0 ? 'primary' : 'default'}
                    />
                    <Chip 
                        label={`${selectedListings.length} annonce(s)`} 
                        color={selectedListings.length > 0 ? 'secondary' : 'default'}
                    />
                </Box>
            </Box>
        </Paper>
    );

    /**
     * Étape 4: Résultats
     */
    const renderResults = () => (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                Résultats de l'action groupée
            </Typography>

            {results && (
                <Box>
                    <Alert severity={results.success ? 'success' : 'error'} sx={{ mb: 3 }}>
                        {results.message}
                    </Alert>

                    {results.details && (
                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                Détails:
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="success" />
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary="Succès"
                                        secondary={`${results.details.successful} élément(s) traité(s)`}
                                    />
                                </ListItem>
                                {results.details.failed > 0 && (
                                    <ListItem>
                                        <ListItemIcon>
                                            <WarningIcon color="error" />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary="Échecs"
                                            secondary={`${results.details.failed} élément(s) en erreur`}
                                        />
                                    </ListItem>
                                )}
                            </List>
                        </Box>
                    )}

                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                                // Télécharger le rapport détaillé
                                const blob = new Blob([JSON.stringify(results, null, 2)], {
                                    type: 'application/json'
                                });
                                const url = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `bulk-action-report-${new Date().toISOString()}.json`;
                                link.click();
                                URL.revokeObjectURL(url);
                            }}
                        >
                            Télécharger le rapport
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleReset}
                        >
                            Nouvelle action
                        </Button>
                    </Box>
                </Box>
            )}
        </Paper>
    );

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <PageHeader
                title="Actions groupées User ↔ Listings"
                subtitle="Effectuez des actions en masse sur les relations utilisateurs-annonces"
                icon={<GroupWorkIcon />}
            />

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Stepper */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Stepper activeStep={activeStep}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Paper>

            {/* Contenu de l'étape courante */}
            <Box sx={{ mb: 3 }}>
                {activeStep === 0 && renderActionSelection()}
                {activeStep === 1 && renderActionConfiguration()}
                {activeStep === 2 && renderTargetSelection()}
                {activeStep === 3 && renderResults()}
            </Box>

            {/* Boutons de navigation */}
            {activeStep < 3 && (
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                            disabled={activeStep === 0}
                            onClick={handleBack}
                        >
                            Précédent
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={!canProceedToNext() || loading}
                        >
                            {loading ? (
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                            ) : null}
                            {activeStep === steps.length - 2 ? 'Exécuter' : 'Suivant'}
                        </Button>
                    </Box>
                </Paper>
            )}
        </Container>
    );
};

export default UserListingsBulkActionsPage;
